import React, { useEffect, useState } from "react";
import { BiArrowBack } from "react-icons/bi";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { base_url } from "../utils/axiosConfig";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { getUserCart, clearCart } from "../features/user/userSlice";
import { toast } from "react-toastify";
import { setAuthenticated, setUser } from "../utils/authAction";
import { LuLogIn } from "react-icons/lu";
import { Modal, Button } from "react-bootstrap";
import { SlActionRedo } from "react-icons/sl";
import { TbCash } from "react-icons/tb";

const validatePincode = async (pincode) => {
  if (!pincode) return false;
  try {
    const response = await axios.get(`${base_url}master/getpin`);
    const pincodedata = response.data;
    return pincodedata.some((item) => item.name.includes(pincode.toString()));
  } catch (error) {
    console.error("Pincode API Error:", error);
    return false;
  }
};

const shippingSchema = yup.object({
  firstName: yup.string().required("First Name is required *"),
  lastName: yup.string().required("Last Name is required *"),
  address: yup.string().required("Address is required *"),
  landmark: yup.string().required("Landmark is required *"),
  city: yup.string().required("City is required *"),
  state: yup.string().required("State is required *"),
  pincode: yup
    .string()
    .required("Pincode is required *")
    .test("is-valid-pincode", "This Pincode is not available", async (value) => await validatePincode(value)),
  phoneNumber: yup
    .string()
    .required("Phone number is required *")
    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits"),
});

const Checkout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartProducts, isAuthenticated, user } = useSelector((state) => state.auth);

  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("COD"); // "COD", "ONLINE", "UPI"
  const [loading, setLoading] = useState(false);
  const [useExistingAddress, setUseExistingAddress] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [pincodeError, setPincodeError] = useState(null);
  const [showCartModal, setShowCartModal] = useState(false);

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const token = JSON.parse(localStorage.getItem("customer"))?.token || localStorage.getItem("token");
        if (!token || !isAuthenticated) return;

        const response = await axios.get(`${base_url}user/getshippingadress`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });

        const addresses = response.data.shippingAddresses || [];
        setSavedAddresses(addresses);

        if (addresses.length > 0) {
          setSelectedAddressId(addresses[0]._id);
          setUseExistingAddress(true);
        } else if (user?.address1) {
          formik.setValues({
            ...formik.values,
            address: user.address1,
            firstName: user.firstname || "",
            lastName: user.lastname || "",
            city: "",
            state: "",
            pincode: user.pincode || "",
            landmark: "",
          });
          setUseExistingAddress(false);
        }
      } catch (error) {
        console.error("Error fetching addresses:", error.response?.data || error.message);
        toast.error("Failed to fetch saved addresses.");
      }
    };

    if (isAuthenticated) fetchAddresses();
  }, [isAuthenticated, user]);

  useEffect(() => {
    const token = localStorage.getItem("token") || JSON.parse(localStorage.getItem("customer"))?.token;
    if (token) {
      dispatch(setAuthenticated(true));
      dispatch(setUser({ token }));
    }
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) dispatch(getUserCart());
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    const storedTotal = localStorage.getItem("checkoutTotal");
    if (storedTotal) {
      setTotalAmount(parseFloat(storedTotal).toFixed(2));
    } else if (cartProducts?.length > 0) {
      const sum = calculateSubTotal();
      setTotalAmount(sum);
    } else {
      const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
      setCartItems(savedCart);
      const sum = calculateSubTotal(savedCart);
      setTotalAmount(sum);
    }
  }, [cartProducts, isAuthenticated]);

  useEffect(() => {
    if (cartProducts?.length > 0) {
      setCartItems(cartProducts);
    } else {
      const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
      setCartItems(savedCart);
    }
  }, [cartProducts, isAuthenticated]);

  const calculateSubTotal = (items = cartItems) => {
    return items
      .reduce((acc, item) => {
        const price = item?.price || item?.ProductId?.offerPrice || item?.ProductId?.CurrentPrice || 0;
        const gstPercent = parseFloat(item?.ProductId?.gst) || 0;
        const gstAmount = (price * item.quantity * gstPercent) / 100;
        return acc + price * item.quantity + gstAmount;
      }, 0)
      .toFixed(2);
  };

  // Step 1: Create pending order in DB to get orderId
  const createPendingOrder = async (orderData) => {
    const token = JSON.parse(localStorage.getItem("customer"))?.token || localStorage.getItem("token");

    try {
      const response = await axios.post(
        `${base_url}user/cart/create-order`,
        {
          ...orderData,
          paymentInfo: {
            method: "RAZORPAY",
            paymentStatus: "Pending",
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        }
      );

      if (response.data.success && response.data.order?._id) {
        return response.data.order._id;
      }
      toast.error("Failed to create order.");
      return null;
    } catch (error) {
      console.error("Pending order creation failed:", error);
      toast.error(error.response?.data?.message || "Failed to create order.");
      setLoading(false);
      return null;
    }
  };

  // Step 2: Create Razorpay order on backend
  const createRazorpayOrder = async () => {
    const token = JSON.parse(localStorage.getItem("customer"))?.token || localStorage.getItem("token");

    try {
      const response = await axios.post(
        `${base_url}user/razorpay/create`,
        { totalPrice: parseFloat(totalAmount) },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data.razorpayOrder; // { id, amount, currency, receipt }
    } catch (error) {
      console.error("Razorpay order creation failed:", error);
      toast.error("Failed to initiate payment.");
      setLoading(false);
      return null;
    }
  };

  // Full Razorpay Payment Flow
  const handleRazorpayPayment = async (orderData) => {
    setLoading(true);

    // 1. Create pending order → get orderId
    const orderId = await createPendingOrder(orderData);
    if (!orderId) return;

    // 2. Create Razorpay order
    const razorpayOrder = await createRazorpayOrder();
    if (!razorpayOrder || !razorpayOrder.id) return;

    const options = {
      key: "rzp_test_RuYcWeKHNQ3bmf", // Replace with your live key in production
      amount: razorpayOrder.amount,
      currency: razorpayOrder.currency || "INR",
      name: "Shakambari",
      description: "Order Payment",
      order_id: razorpayOrder.id,
      receipt: razorpayOrder.receipt,
      // Force UPI method if selected
      ...(paymentMethod === "UPI" && { method: "upi" }),
      handler: async (response) => {
        try {
          const token = JSON.parse(localStorage.getItem("customer"))?.token || localStorage.getItem("token");

          // 3. Verify payment
          const verifyResponse = await axios.post(
            `${base_url}user/razorpay/verify`,
            {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              orderId: orderId,
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (verifyResponse.data.success) {
            toast.success("Payment successful! Order confirmed.");
            dispatch(clearCart());
            localStorage.removeItem("cart");
            localStorage.removeItem("checkoutTotal");
            setCartItems([]);
            navigate("/product"); // or your success page
          } else {
            toast.error("Payment verification failed.");
          }
        } catch (error) {
          toast.error("Payment verification error.");
          console.error(error);
        } finally {
          setLoading(false);
        }
      },
      prefill: {
        name: `${formik.values.firstName} ${formik.values.lastName}`,
        email: user?.email || "",
        contact: formik.values.phoneNumber,
      },
      theme: { color: "#3399cc" },
      modal: {
        ondismiss: () => {
          setLoading(false);
          toast.info("Payment cancelled.");
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const formik = useFormik({
    initialValues: {
      firstName: user?.firstname || "",
      lastName: user?.lastname || "",
      address: user?.address1 || "",
      landmark: "",
      city: "",
      state: "",
      pincode: user?.pincode || "",
      phoneNumber: user?.phoneNumber || "",
      deliveryInstruction: "",
    },
    validationSchema: useExistingAddress ? null : shippingSchema,
    validateOnChange: !useExistingAddress,
    validateOnBlur: !useExistingAddress,
    onSubmit: () => {},
  });

  const handlePincodeChange = async (e) => {
    const pincode = e.target.value;
    formik.handleChange(e);
    if (pincode && !useExistingAddress) {
      try {
        const isValid = await validatePincode(pincode);
        setPincodeError(isValid ? null : "We currently do not deliver to this pincode.");
      } catch (error) {
        setPincodeError("Error validating pincode");
      }
    }
  };

  const handleLoginRedirect = () => {
    localStorage.setItem("checkoutFormData", JSON.stringify(formik.values));
    localStorage.setItem("checkoutRedirect", "/checkout");
    if (cartItems?.length > 0) localStorage.setItem("tempCart", JSON.stringify(cartItems));
    window.location.href = "/login";
  };

  useEffect(() => {
    if (isAuthenticated) {
      const savedForm = localStorage.getItem("checkoutFormData");
      const savedCart = localStorage.getItem("tempCart");
      if (savedForm) formik.setValues(JSON.parse(savedForm));
      if (!cartProducts.length && savedCart) setCartItems(JSON.parse(savedCart));
      localStorage.removeItem("checkoutFormData");
      localStorage.removeItem("tempCart");
    }
  }, [isAuthenticated]);

  const handlePlaceOrder = async () => {
    if (!isAuthenticated) {
      toast.error("Please log in to place order.");
      return;
    }

    if (useExistingAddress && !selectedAddressId) {
      toast.error("Please select a delivery address.");
      return;
    }

    if (!useExistingAddress) {
      await formik.validateForm();
      if (Object.keys(formik.errors).length > 0) {
        toast.error("Please fix the shipping form errors.");
        return;
      }
    }

    setLoading(true);

    const cartItemsToUse = cartItems.length > 0 ? cartItems : JSON.parse(localStorage.getItem("cart")) || [];
    if (cartItemsToUse.length === 0) {
      toast.error("Your cart is empty.");
      setLoading(false);
      return;
    }

    let shippingInfo = useExistingAddress
      ? savedAddresses.find((addr) => addr._id === selectedAddressId)
      : formik.values;

    if (useExistingAddress && shippingInfo) {
      shippingInfo = {
        ...shippingInfo,
        firstName: shippingInfo.firstName || user?.firstname || "",
        lastName: shippingInfo.lastName || user?.lastname || "",
        phoneNumber: formik.values.phoneNumber || user?.phoneNumber || "",
      };
    }

    const orderData = {
      shippingInfo,
      orderItems: cartItemsToUse.map((item) => ({
        product: item.ProductId._id,
        quantity: item.quantity,
        price: item.price,
      })),
      totalPrice: parseFloat(totalAmount),
      useExistingAddress,
      addressId: useExistingAddress ? selectedAddressId : null,
      deliveryInstruction: shippingInfo.deliveryInstruction || "",
      phoneNumber: shippingInfo.phoneNumber || "",
    };

    if (paymentMethod === "COD") {
      const token = JSON.parse(localStorage.getItem("customer"))?.token || localStorage.getItem("token");
      try {
        const response = await axios.post(
          `${base_url}user/cart/create-order`,
          {
            ...orderData,
            paymentInfo: { method: "COD", paymentStatus: "Pending" },
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          toast.success("Order placed successfully!");
          dispatch(clearCart());
          localStorage.removeItem("cart");
          localStorage.removeItem("checkoutTotal");
          setCartItems([]);
          navigate("/product");
        }
      } catch (error) {
        toast.error("Order placement failed.");
      } finally {
        setLoading(false);
      }
    } else {
      // ONLINE or UPI → both use Razorpay
      await handleRazorpayPayment(orderData);
    }
  };

  return (
    <>
      <div className="checkout-container">
        <div className="checkout-wrapper">
          <div className="row g-4">
            {/* Left Section - Shipping Details */}
            <div className="col-lg-8">
              <div className="shipping-card">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="section-title mb-0">Delivery Address</h2>
                  {!user ? (
                    <button
                      onClick={handleLoginRedirect}
                      className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2 rounded-rounded shadow-sm"
                    >
                      <span>Login</span>
                      <LuLogIn size={18} />
                    </button>
                  ) : (
                    <span className="welcome-badge">Welcome, {user.email}</span>
                  )}
                </div>

                {!isAuthenticated && (
                  <p className="text-danger mb-2">
                    Please login to continue with shipping <SlActionRedo />
                  </p>
                )}

                {isAuthenticated && savedAddresses.length > 0 && (
                  <div className="mb-4">
                    <h3 className="subtitle">Select Saved Address</h3>
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="useExistingAddress"
                        checked={useExistingAddress}
                        onChange={() => setUseExistingAddress(!useExistingAddress)}
                      />
                      <label className="form-check-label" htmlFor="useExistingAddress">
                        Use saved address
                      </label>
                    </div>
                    {useExistingAddress && (
                      <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                        {savedAddresses.map((address) => (
                          <div key={address._id} className="address-item">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="savedAddress"
                                id={`address-${address._id}`}
                                value={address._id}
                                checked={selectedAddressId === address._id}
                                onChange={(e) => setSelectedAddressId(e.target.value)}
                              />
                              <label className="form-check-label w-100 p-2" htmlFor={`address-${address._id}`}>
                                <div className="small">
                                  {`${address.firstName || ""} ${address.lastName || ""}`}<br />
                                  {`${address.address}, ${address.landmark || ""}`}<br />
                                  {`${address.city}, ${address.state} - ${address.pincode}`}
                                </div>
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {(!isAuthenticated || savedAddresses.length === 0 || !useExistingAddress) && (
                  <form>
                    <div className="row g-1">
                      {[
                        { name: "firstName", placeholder: "First Name", col: "col-md-6" },
                        { name: "lastName", placeholder: "Last Name", col: "col-md-6" },
                        { name: "landmark", placeholder: "Landmark", col: "col-md-6" },
                        { name: "city", placeholder: "City", col: "col-md-6" },
                        { name: "state", placeholder: "State", col: "col-md-6" },
                        { name: "pincode", placeholder: "Pincode", onChange: handlePincodeChange, col: "col-md-6" },
                      ].map(({ name, placeholder, onChange, col }) => (
                        <div className={col} key={name}>
                          <input
                            type="text"
                            name={name}
                            className="form-input"
                            placeholder={placeholder}
                            onChange={onChange || formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values[name]}
                            disabled={!isAuthenticated}
                          />
                          {formik.touched[name] && formik.errors[name] && (
                            <div className="error-text">{formik.errors[name]}</div>
                          )}
                          {name === "pincode" && pincodeError && <div className="error-text">{pincodeError}</div>}
                        </div>
                      ))}
                      <div className="col-md-6">
                        <input
                          type="text"
                          name="phoneNumber"
                          className="form-input"
                          placeholder="Phone Number"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.phoneNumber}
                          disabled={!isAuthenticated}
                        />
                        {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                          <div className="error-text">{formik.errors.phoneNumber}</div>
                        )}
                      </div>
                      <div className="col-12">
                        <input
                          type="text"
                          name="address"
                          className="form-input"
                          placeholder="Full Address"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.address}
                          disabled={!isAuthenticated}
                        />
                        {formik.touched.address && formik.errors.address && (
                          <div className="error-text">{formik.errors.address}</div>
                        )}
                      </div>
                      <div className="col-12">
                        <textarea
                          name="deliveryInstruction"
                          className="form-input"
                          placeholder="Delivery Instructions (optional)"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.deliveryInstruction}
                          rows="3"
                          disabled={!isAuthenticated}
                        />
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>

            {/* Right Section - Order Summary */}
            <div className="col-lg-4">
              <div className="order-summary-card">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="section-title mb-0">Order Summary</h2>
                  <Link to="/Cart" className="text-primary text-decoration-none d-flex align-items-center small">
                    <BiArrowBack className="me-1" /> Back to Cart
                  </Link>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-4">
                  <p className="mb-0 text-muted">
                    Total Items: <strong>{cartItems.reduce((acc, item) => acc + (item.quantity || 0), 0)}</strong>
                  </p>
                  <button className="btn btn-primary p-0 text-light py-1 px-3" onClick={() => setShowCartModal(true)}>
                    View Details
                  </button>
                </div>

                <div className="mb-4">
                  <h5 className="mb-3 fw-semibold">Payment Methods</h5>

                  {/* Cash on Delivery */}
                  <div className={`form-check border rounded p-3 mb-3 ${paymentMethod === "COD" ? "border-primary" : ""}`}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="paymentMethod"
                      id="cashOnDelivery"
                      checked={paymentMethod === "COD"}
                      onChange={() => setPaymentMethod("COD")}
                      disabled={!isAuthenticated || loading}
                    />
                    <label className="form-check-label w-100 ms-2" htmlFor="cashOnDelivery">
                      <div className="d-flex align-items-center">
                        <span className="me-3 text-success">
                          <TbCash size={22} />
                        </span>
                        <div>
                          <div className="fw-medium">Cash on Delivery</div>
                          <small className="text-muted">Pay with cash when your order is delivered</small>
                        </div>
                      </div>
                    </label>
                  </div>

                  {/* Pay Online (All methods) */}
                  <div className={`form-check border rounded p-3 mb-3 ${paymentMethod === "ONLINE" ? "border-primary" : ""}`}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="paymentMethod"
                      id="onlinePayment"
                      checked={paymentMethod === "ONLINE"}
                      onChange={() => setPaymentMethod("ONLINE")}
                      disabled={!isAuthenticated || loading}
                    />
                    <label className="form-check-label w-100 ms-2" htmlFor="onlinePayment">
                      <div className="d-flex align-items-center">
                        <span className="me-3">
                          <img
                            src="https://media.tradly.app/images/marketplace/34521/razor_pay_icon-ICtywSbN.png"
                            alt="Razorpay"
                            style={{ height: "40px" }}
                          />
                        </span>
                        <div>
                          <div className="fw-medium">Pay Online (Card, Netbanking, Wallet)</div>
                          <small className="text-muted">Via Razorpay</small>
                        </div>
                      </div>
                    </label>
                  </div>

                  {/* UPI Only */}
                  <div className={`form-check border rounded p-3 ${paymentMethod === "UPI" ? "border-primary" : ""}`}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="paymentMethod"
                      id="upiPayment"
                      checked={paymentMethod === "UPI"}
                      onChange={() => setPaymentMethod("UPI")}
                      disabled={!isAuthenticated || loading}
                    />
                    <label className="form-check-label w-100 ms-2" htmlFor="upiPayment">
                      <div className="d-flex align-items-center">
                        <span className="me-3">
                        <img src="https://images.seeklogo.com/logo-png/33/1/unified-payment-interface-upi-logo-png_seeklogo-333088.png"
                        style={{ height: "40px" }}
                         />
                        </span>
                        <div>
                          <div className="fw-medium">Pay with UPI Only</div>
                          <small className="text-muted">Google Pay, PhonePe, BHIM, Paytm, etc.</small>
                        </div>
                      </div>
                    </label>
                  </div>

                  {!isAuthenticated && (
                    <small className="text-danger d-block mt-3">
                      Please log in to choose a payment method.
                    </small>
                  )}
                </div>

                <div className="text-center border-top pt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="mb-0 total-amount">₹{totalAmount}</h3>
                  </div>
                  <button
                    className="btn btn-primary-custom w-100 d-flex align-items-center justify-content-center"
                    disabled={!isAuthenticated || loading || (useExistingAddress && !selectedAddressId)}
                    onClick={handlePlaceOrder}
                  >
                    {loading ? (
                      <>
                        <div className="spinner-custom"></div>
                        Processing...
                      </>
                    ) : (
                      "Place Order"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
          </div>
        )}

        <Modal show={showCartModal} onHide={() => setShowCartModal(false)} centered size="md">
          <Modal.Header closeButton className="border-bottom">
            <Modal.Title className="h5 mb-0">Your Cart Items</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
            {cartItems.length > 0 ? (
              cartItems.map((item, i) => (
                <div key={i} className="modal-cart-item">
                  <img
                    src={item?.ProductId?.images?.[0]?.url ? `${base_url}${item.ProductId.images[0].url}` : "/default.jpg"}
                    className="cart-item-img"
                    alt={item?.ProductId?.title}
                  />
                  <div className="item-details">
                    <div className="item-title">{item?.ProductId?.title}</div>
                    <div className="small text-muted">Qty: {item?.quantity}</div>
                    <div className="item-price">₹{item?.price}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-danger py-4">
                <p className="mb-0">Your cart is empty.</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-top">
            <Button variant="secondary" onClick={() => setShowCartModal(false)}>
              Close
            </Button>
            <Link to="/Cart" className="btn btn-primary">
              Go to Cart
            </Link>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default Checkout;